/**
 * Central gamification configuration.
 * XP/Level math, cosmetic unlock rules, badge definitions, and fallback defaults.
 * Live player data comes from /api/mastery/{studentId} via App.js fetchPlayerData().
 */

// ── XP / Level math ──────────────────────────────────────────────────────────
//
// Square-root curve: levels slow down as XP grows, rewarding early momentum
// while keeping high-level milestones meaningful.
//
//   current_level      = floor(sqrt(xp / 100)) + 1
//   xp_for_level(n)    = (n - 1)² × 100   ← XP floor to reach level n
//   xp_for_next_level  = current_level²    × 100   ← XP ceiling for current level
//
// Examples:
//   0 XP   → Level 1  (threshold 0 → 100)
//   100 XP → Level 2  (threshold 100 → 400)
//   400 XP → Level 3  (threshold 400 → 900)
//   900 XP → Level 4  (threshold 900 → 1600)

export const xpToLevel = (xp) => Math.floor(Math.sqrt(Math.max(0, xp) / 100)) + 1;

/** XP needed to enter level n (the lower threshold). */
export const xpFloorForLevel = (n) => Math.pow(Math.max(1, n) - 1, 2) * 100;

/** XP needed to enter the NEXT level (the upper threshold of the current level). */
export const xpCeilForLevel  = (n) => Math.pow(Math.max(1, n), 2) * 100;

/** XP earned within the current level (resets to 0 at each level boundary). */
export const xpWithinLevel = (xp) => {
  const level = xpToLevel(xp);
  return Math.max(0, xp - xpFloorForLevel(level));
};

/** Total XP span of the current level (varies per level with the curve). */
export const xpRangeForLevel = (level) => xpCeilForLevel(level) - xpFloorForLevel(level);

// Legacy constant kept so any existing import of XP_PER_LEVEL doesn't crash —
// it is no longer used for bar width calculations (each level has its own range).
export const XP_PER_LEVEL = 500;

// ── Fallback player data (shown while /api/mastery is loading or unauthenticated) ─

export const STUB_PLAYER = {
  name:             'M. Brooke',
  cohort:           'Year 12 HL',
  lifetimeXP:       0,
  equippedAvatarId: 'avatar_default',
  equippedRingId:   'ring_none',
  equippedPetId:    'pet_none',
  stats: {
    cleanRuns:        0,
    totalExercises:   0,
    currentStreak:    0,
    maxStreak:        0,
  },
  earnedBadgeIds: [],
};

// ── Cosmetics config ─────────────────────────────────────────────────────────
// Add new items here — the Locker renders them automatically.

export const COSMETICS_CONFIG = {
  avatars: [
    { id: 'avatar_default',    label: 'Coder',      emoji: '👨‍💻', unlockLevel: 1  },
    { id: 'avatar_explorer',   label: 'Explorer',   emoji: '🔭',  unlockLevel: 3  },
    { id: 'avatar_architect',  label: 'Architect',  emoji: '🏗️',  unlockLevel: 5  },
    { id: 'avatar_hacker',     label: 'Hacker',     emoji: '🕶️',  unlockLevel: 8  },
    { id: 'avatar_wizard',     label: 'Wizard',     emoji: '🧙',  unlockLevel: 12 },
    { id: 'avatar_ninja',      label: 'Ninja',      emoji: '🥷',  unlockLevel: 16 },
    { id: 'avatar_scientist',  label: 'Scientist',  emoji: '🧑‍🔬', unlockLevel: 20 },
    { id: 'avatar_legend',     label: 'Legend',     emoji: '🌟',  unlockLevel: 25 },
  ],
  rings: [
    { id: 'ring_none',    label: 'No Ring',  cssColor: null,                    unlockLevel: 1  },
    { id: 'ring_bronze',  label: 'Bronze',   cssColor: 'oklch(0.65 0.10 65)',   unlockLevel: 1  },
    { id: 'ring_silver',  label: 'Silver',   cssColor: 'oklch(0.72 0.04 220)',  unlockLevel: 4  },
    { id: 'ring_gold',    label: 'Gold',     cssColor: 'oklch(0.82 0.14 85)',   unlockLevel: 7  },
    { id: 'ring_purple',  label: 'Amethyst', cssColor: 'oklch(0.68 0.16 295)',  unlockLevel: 10 },
    { id: 'ring_emerald', label: 'Emerald',  cssColor: 'oklch(0.62 0.18 145)',  unlockLevel: 13 },
    { id: 'ring_diamond', label: 'Diamond',  cssColor: 'oklch(0.88 0.06 220)',  unlockLevel: 18 },
    { id: 'ring_prism',   label: 'Prism',    cssColor: 'oklch(0.72 0.25 300)',  unlockLevel: 24 },
  ],
  pets: [
    { id: 'pet_none',    label: 'No Pet',      emoji: '—',  unlockLevel: 1  },
    { id: 'pet_duck',    label: 'Debug Duck',  emoji: '🦆', unlockLevel: 2  },
    { id: 'pet_owl',     label: 'Code Owl',    emoji: '🦉', unlockLevel: 6  },
    { id: 'pet_coffee',  label: 'Coffee Cup',  emoji: '☕', unlockLevel: 9  },
    { id: 'pet_penguin', label: 'Penguin',     emoji: '🐧', unlockLevel: 12 },
    { id: 'pet_dragon',  label: 'Data Dragon', emoji: '🐉', unlockLevel: 15 },
    { id: 'pet_phoenix', label: 'Phoenix',     emoji: '🔥', unlockLevel: 22 },
  ],
};

// ── Badges config ────────────────────────────────────────────────────────────

export const BADGES_CONFIG = [
  // ── Coding habits ──
  { id: 'first_try',        label: 'First Try',        emoji: '🎯', description: 'Passed an exercise on your first attempt'         },
  { id: 'clean_run',        label: 'Clean Run',        emoji: '✨', description: 'Ran code 3+ times and passed first try'           },
  { id: 'test_pilot',       label: 'Test Pilot',       emoji: '✈️', description: 'Ran code 2+ times on an exercise before passing'  },
  { id: 'curious_tinkerer', label: 'Curious Tinkerer', emoji: '🔧', description: 'Completed 5 or more exercises'                    },
  { id: 'concept_master',   label: 'Concept Master',   emoji: '🧠', description: 'Achieved a mastery score of 80+ on a topic'       },
  { id: 'polyglot',         label: 'Polyglot',         emoji: '🌐', description: 'Completed tasks in both Python and Java'          },
  { id: 'streak',           label: '5-Day Streak',     emoji: '🔥', description: 'Practise 5 days in a row'                        },

  // ── Python track ──
  { id: 'python_novice',       label: 'Python Novice',       emoji: '🐍', description: 'Completed your first Python unit'          },
  { id: 'python_practitioner', label: 'Python Practitioner', emoji: '🐍', description: 'Completed all SL Python units'              },
  { id: 'python_virtuoso',     label: 'Python Virtuoso',     emoji: '🐍', description: '100% of Theme B Python, SL and HL'          },

  // ── Java track ──
  { id: 'java_novice',       label: 'Java Novice',       emoji: '☕', description: 'Completed your first Java unit'                 },
  { id: 'java_practitioner', label: 'Java Practitioner', emoji: '☕', description: 'Completed all SL Java units'                     },
  { id: 'java_virtuoso',     label: 'Java Virtuoso',     emoji: '☕', description: '100% of Theme B Java, SL and HL'                 },

  // ── Theory (Theme A) ──
  { id: 'systems_scholar',    label: 'Systems Scholar',    emoji: '🖥️', description: 'A1 Computer fundamentals mastery 80%+'       },
  { id: 'network_navigator',  label: 'Network Navigator',  emoji: '📡', description: 'A2 Networks mastery 80%+'                    },
  { id: 'database_architect', label: 'Database Architect', emoji: '🗄️', description: 'A3 Databases mastery 80%+'                   },
  { id: 'ml_explorer',        label: 'ML Explorer',        emoji: '🤖', description: 'A4 Machine learning mastery 80%+'            },
  { id: 'theme_a_graduate',   label: 'Theme A Graduate',   emoji: '🎓', description: 'Overall Theme A mastery 80%+'                },

  // ── Exam readiness ──
  { id: 'paper2_ready', label: 'Paper 2 Ready', emoji: '🏆', description: '100% Theme B completion in Python or Java'              },

  // ── Level milestones ──
  { id: 'rising_star',       label: 'Rising Star',       emoji: '⭐', description: 'Reached Level 5'                                },
  { id: 'dedicated_learner', label: 'Dedicated Learner', emoji: '🚀', description: 'Reached Level 10'                               },
  { id: 'seasoned_coder',    label: 'Seasoned Coder',    emoji: '💪', description: 'Reached Level 15'                               },
  { id: 'ib_cs_master',      label: 'IB CS Master',      emoji: '👑', description: 'Reached Level 20'                               },
];

// ── Per-topic data ────────────────────────────────────────────────────────────
// Two strictly separate numbers per subtopic:
//   completionProgress  — (completedExercises / totalExercises) × 100. Never goes down.
//   masteryScore        — 0-1 float, decays over time when exercises aren't revisited.
//
// The SyllabusMatrix bar reads completionProgress only.
// masteryScore < MASTERY_DECAY_THRESHOLD triggers the "Needs Review" warning.

export const MASTERY_DECAY_THRESHOLD = 0.80; // below this → flag row + NeedsReview strip

// ── Theme B progress helper ──────────────────────────────────────────────────

export function calcThemeBProgress(topicDataMap) {
  const entries = Object.values(topicDataMap);
  if (entries.length === 0) return 0;
  const completed = entries.reduce((s, t) => s + t.completedExercises, 0);
  const total     = entries.reduce((s, t) => s + t.totalExercises, 0);
  return total > 0 ? (completed / total) * 100 : 0;
}

// ── Derive "needs review" items from local topic data ─────────────────────────
// Returns topics whose masteryScore has decayed below the threshold,
// formatted for NeedsReviewCard consumption.

export function deriveNeedsReview(topicDataMap, allTopics) {
  return allTopics
    .filter(t => {
      const data = topicDataMap[t.id];
      if (!data) return false;
      const isComplete = data.totalExercises > 0 && data.completedExercises >= data.totalExercises;
      return isComplete && data.masteryScore !== null && data.masteryScore < MASTERY_DECAY_THRESHOLD;
    })
    .map(t => ({
      id:      t.id,
      name:    t.title.replace(`${t.id} `, ''),
      mastery: topicDataMap[t.id].masteryScore,
    }));
}
